import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, Home } from "lucide-react";

const MAX_ATTEMPTS = 10;
const POLL_INTERVAL_MS = 2000;

export default function BillingSuccess() {
  const nav = useNavigate();
  const [search] = useSearchParams();
  const sessionId = search.get("session_id");
  const { refreshBilling } = useAuth();
  const [state, setState] = useState("polling"); // polling | paid | failed | expired
  const [info, setInfo] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let stopped = false;

    const poll = async () => {
      if (stopped) return;
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/billing/checkout-status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") {
          setState("paid");
          refreshBilling();
          return;
        }
        if (data.status === "expired") {
          setState("expired");
          return;
        }
      } catch (err) {
        // keep polling until timeout
      }
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setState("failed");
        return;
      }
      setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();
    return () => { stopped = true; };
  }, [sessionId, refreshBilling]);

  return (
    <div className="min-h-screen bg-hearth-cream flex items-center justify-center px-6">
      <div
        data-testid="billing-success-card"
        className="w-full max-w-md bg-white rounded-3xl border border-hearth-line p-10 text-center shadow-lg"
      >
        {state === "polling" && (
          <>
            <Loader2 className="animate-spin mx-auto text-hearth-clay" size={40} />
            <h1 className="font-display text-3xl font-semibold mt-4">Just a moment…</h1>
            <p className="text-hearth-mute mt-2">
              Confirming your payment with Stripe. This usually takes a few seconds.
            </p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="w-16 h-16 rounded-full bg-hearth-sage/25 grid place-items-center mx-auto">
              <CheckCircle2 className="text-hearth-sageDark" size={36} />
            </div>
            <h1 className="font-display text-3xl font-semibold mt-4">Welcome home.</h1>
            <p className="text-hearth-mute mt-2">
              Your {info?.plan === "annual" ? "annual" : "monthly"} plan is active
              {info?.amount && info?.currency ? ` — ${info.amount} ${info.currency.toUpperCase()}` : ""}.
              Thank you for keeping the kettle on.
            </p>
            <Button
              data-testid="billing-success-continue"
              onClick={() => nav("/")}
              className="mt-6 h-12 rounded-full bg-hearth-terracotta hover:bg-hearth-terracottaDark text-white px-8"
            >
              <Home size={16} className="mr-2" /> Back to the house
            </Button>
          </>
        )}
        {(state === "failed" || state === "expired") && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-100 grid place-items-center mx-auto">
              <XCircle className="text-red-500" size={36} />
            </div>
            <h1 className="font-display text-3xl font-semibold mt-4">
              {state === "expired" ? "Session expired" : "We couldn't confirm the payment"}
            </h1>
            <p className="text-hearth-mute mt-2">
              {state === "expired"
                ? "Your checkout session expired before payment was completed."
                : "This can be a slow webhook — refresh the page in a minute or try again."}
            </p>
            <Button
              data-testid="billing-success-retry"
              onClick={() => nav("/upgrade")}
              variant="outline"
              className="mt-6 h-12 rounded-full px-8"
            >
              Try again
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
